import { type ComponentType, lazy, type LazyExoticComponent } from "react";

/** Which session may prefetch this chunk. Shared pages load on every cold start. */
export type RouteBundleGroup = "shared" | "client" | "sfs" | "reporting";

interface RegisteredPageLoader {
  group: RouteBundleGroup;
  load: () => Promise<unknown>;
}

const registry: RegisteredPageLoader[] = [];

/** Delays before the 2nd and 3rd attempt. Keeps a flaky chunk fetch off the error path. */
const RETRY_DELAYS_MS: readonly number[] = [150, 400];

export function getRegisteredPageLoaders(): readonly RegisteredPageLoader[] {
  return registry;
}

export function resetRegisteredPageLoadersForTests(): void {
  registry.length = 0;
}

export async function loadWithRetry<T>(
  factory: () => Promise<T>,
  delaysMs: readonly number[] = RETRY_DELAYS_MS
): Promise<T> {
  let lastError: unknown;
  const attempts = delaysMs.length + 1;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;
      const delay = delaysMs[attempt];
      if (delay == null) {
        break;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, delay);
      });
    }
  }

  throw lastError;
}

/**
 * Lazy-load a route page module (default export).
 * The loader is cached and registered so the background preloader can warm the
 * same promise React.lazy uses, without pulling the page into the entry bundle.
 */
export function lazyPage<T extends ComponentType<object>>(
  factory: () => Promise<{ default: T }>,
  group: RouteBundleGroup = "shared"
): LazyExoticComponent<T> {
  let pending: Promise<{ default: T }> | undefined;

  const load = (): Promise<{ default: T }> => {
    if (!pending) {
      pending = loadWithRetry(factory).catch((error: unknown) => {
        pending = undefined;
        throw error;
      });
    }
    return pending;
  };

  registry.push({ group, load });
  return lazy(load);
}
